import { Column } from '@unistocks/components/layout/flex';
import { Paragraph } from '@unistocks/components/text/text';
import {
  ChartHeader,
  ChartHeaderProps
} from '@unistocks/features/portfolios/components/charts/chart-header';
import { formatCurrency, formatDate, formatPercentage, t } from '@unistocks/localization';
import { DateLike } from '@unistocks/types/date-like';
import React from 'react';

/** Shows 2 decimals for values below 1000, otherwise whole numbers. */
const getFractionalDigitsOptions = (value: number) => (Math.abs(value) < 1000 ? 2 : 0);

export type LineChartHeaderProps = ChartHeaderProps & {
  /** First date in the range (timestamp) */
  firstDate?: DateLike;
  /** Last date in the range (timestamp) */
  lastDate?: DateLike;
  /** Total change percentage */
  totalChangePercent: number;
  /** Total change in nominal value */
  totalChangeNominal: number;
  /** Current value (total portfolio value or latest price) */
  totalValue: number;
  /** Currency code for formatting */
  currency: string;
  /** Label for the value stat (defaults to '::Chart:Value') */
  valueLabel?: string;
};

export const LineChartHeader: React.FC<LineChartHeaderProps> = ({
  firstDate,
  lastDate,
  totalChangePercent,
  totalChangeNominal,
  totalValue,
  currency,
  valueLabel,
  ...props
}) => {
  return (
    <ChartHeader
      heading={`${firstDate ? formatDate(firstDate) : ''} - ${lastDate ? formatDate(lastDate) : ''}`}
      {...props}
    >
      <Column alignItems={'center'}>
        <Paragraph size={'$3'} userSelect="none">
          {t('::Chart:ChangePercent')}
        </Paragraph>
        <Paragraph
          size={'$3'}
          fontWeight={'$8'}
          color={totalChangePercent < 0 ? '$negative' : '$positive'}
          userSelect="none"
        >
          {formatPercentage(totalChangePercent, { signDisplay: 'exceptZero' })}
        </Paragraph>
      </Column>

      <Column alignItems={'center'}>
        <Paragraph size={'$3'} userSelect="none">
          {t('::Chart:ChangeNominal', { 0: currency })}
        </Paragraph>
        <Paragraph
          size={'$3'}
          fontWeight={'$8'}
          color={totalChangeNominal < 0 ? '$negative' : '$positive'}
          userSelect="none"
        >
          {formatCurrency(totalChangeNominal, currency, {
            signDisplay: 'exceptZero',
            minimumFractionDigits: getFractionalDigitsOptions(totalChangeNominal),
            maximumFractionDigits: getFractionalDigitsOptions(totalChangeNominal)
          })}
        </Paragraph>
      </Column>

      <Column alignItems={'center'}>
        <Paragraph size={'$3'} userSelect="none">
          {valueLabel ?? t('::Chart:Value')}
        </Paragraph>
        <Paragraph size={'$3'} fontWeight={'$8'} userSelect="none">
          {formatCurrency(totalValue, currency, {
            minimumFractionDigits: getFractionalDigitsOptions(totalValue),
            maximumFractionDigits: getFractionalDigitsOptions(totalValue)
          })}
        </Paragraph>
      </Column>
    </ChartHeader>
  );
};
